<template>
  <div class="app-time-field">
    <div v-if="label" class="field-label">
      <v-icon v-if="labelIcon" size="18">{{ labelIcon }}</v-icon>
      <span>{{ label }}</span>
    </div>

    <v-text-field
      :model-value="displayValue"
      :class="fieldClass"
      :variant="variant"
      :flat="flat"
      :hide-details="hideDetails"
      readonly
      v-bind="fieldAttrs"
    >
      <v-menu
        v-model="showMenu"
        activator="parent"
        :close-on-content-click="false"
        min-width="0"
      >
        <v-time-picker v-model="model" />
      </v-menu>
    </v-text-field>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, useAttrs, watch } from "vue";

defineOptions({
  inheritAttrs: false,
});

const model = defineModel<string | null>();

const props = withDefaults(
  defineProps<{
    variant?: "solo" | "filled" | "outlined" | "plain" | "solo-filled" | "solo-inverted" | "underlined";
    flat?: boolean;
    hideDetails?: boolean | "auto";
    label?: string;
    labelIcon?: string;
  }>(),
  {
    variant: "solo",
    flat: true,
    hideDetails: "auto",
    label: "",
    labelIcon: "",
  },
);

const attrs = useAttrs();
const showMenu = ref(false);

const fieldClass = computed(() => ["app-time-field__input", attrs.class]);
const fieldAttrs = computed(() => {
  const { class: _class, ...rest } = attrs;
  return rest;
});

const displayValue = computed(() => formatTime(model.value));

watch(model, () => {
  if (showMenu.value && model.value) {
    showMenu.value = false;
  }
});

function formatTime(value: string | null) {
  if (!value) return "";
  const raw = String(value).trim();
  if (!raw.includes(":")) return raw;

  const [hoursRaw, minutes = "00"] = raw.split(":");
  const hours = Number(hoursRaw);
  if (Number.isNaN(hours)) return raw;

  const suffix = hours >= 12 ? "PM" : "AM";
  const formattedHour = ((hours + 11) % 12) + 1;
  return `${formattedHour}:${minutes.padStart(2, "0")} ${suffix}`;
}
</script>

<style scoped>
.app-time-field {
  width: 100%;
}

.field-label {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: var(--color-subtitle);
  font-weight: 500;
  font-size: 14px;
  margin-bottom: 6px;
  padding: 0 10px;
}

.app-time-field__input {
  --v-input-control-height: 48px;
  --v-input-padding-top: 0px;
  --v-field-padding-top: 0px;
  --v-field-padding-bottom: 0px;
  --v-field-input-padding-top: 0px;
  --v-field-input-padding-bottom: 0px;
  --v-field-padding-start: 12px;
  --v-field-padding-end: 12px;
}

.app-time-field__input :deep(.v-field) {
  border: 1px solid #eaeaea;
}

.app-time-field__input :deep(.v-field__input) {
  font-size: 0.875rem;
}

.app-time-field__input :deep(.v-input--density-default .v-field--variant-outlined),
.app-time-field__input :deep(.v-input--density-default .v-field--single-line),
.app-time-field__input :deep(.v-input--density-default .v-field--no-label) {
  --v-field-padding-bottom: 0px !important;
}

.app-time-field__input :deep(.v-input--density-default .v-field--variant-solo),
.app-time-field__input :deep(.v-input--density-default .v-field--variant-solo-inverted),
.app-time-field__input :deep(.v-input--density-default .v-field--variant-solo-filled),
.app-time-field__input :deep(.v-input--density-default .v-field--variant-filled) {
  --v-input-control-height: 48px;
  --v-field-padding-bottom: 0px !important;
}

.app-time-field__input :deep(.v-messages__message) {
  color: #dc2626 !important;
  font-size: 13px;
}
</style>
